import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Container,
  Title,
  Text,
  Button,
  Group,
  Grid,
  Image,
  Badge,
  Paper,
  Timeline,
  Divider,
  Stack,
  Table,
  Loader,
  Alert,
  Anchor,
  Card
} from '@mantine/core';
import {
  IconArrowLeft,
  IconFileInvoice,
  IconTruckDelivery,
  IconPackage,
  IconShield,
  IconAlertCircle,
  IconCalendarEvent,
  IconClipboard,
  IconBox,
  IconCheck,
  IconUser,
  IconMapPin
} from '@tabler/icons-react';

// Order type definition (expanded from MyOrders.tsx)
interface OrderItem {
  id: string;
  name: string;
  image: string;
  quantity: number;
  price: number;
}

interface OrderEvent {
  date: string;
  title: string;
  description: string;
}

interface Order {
  id: string;
  date: string;
  status: 'processing' | 'shipped' | 'delivered' | 'cancelled';
  total: number;
  items: OrderItem[];
  events: OrderEvent[];
  trackingNumber?: string;
  carrier?: string;
  estimatedDelivery?: string;
  shippingMethod: string;
  paymentMethod: string;
  shippingAddress: {
    name: string;
    street: string;
    city: string;
    state: string;
    postalCode: string;
    country: string;
  };
}

const OrderDetail = () => {
  const { id } = useParams<{ id: string }>();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);

  // Mock data for order details
  useEffect(() => {
    // Simulate API call
    setTimeout(() => {
      if (id) {
        // In a real app, this would fetch order details from the server
        const mockOrder: Order = {
          id: id,
          date: '2025-04-05T14:30:00Z',
          status: 'processing',
          total: 127.47,
          items: [
            {
              id: 'p1',
              name: 'Ceylon Premium Tea (Black)',
              image: 'https://placehold.co/300x300?text=Ceylon+Tea',
              quantity: 2,
              price: 24.99
            },
            {
              id: 'p3',
              name: 'Cinnamon Sticks (Premium Grade)',
              image: 'https://placehold.co/300x300?text=Cinnamon',
              quantity: 3,
              price: 15.99
            }
          ],
          events: [
            {
              date: '2025-04-05T14:30:00Z',
              title: 'Order Placed',
              description: 'Your order has been received and is being processed.'
            },
            {
              date: '2025-04-05T14:35:00Z',
              title: 'Payment Confirmed',
              description: 'Payment has been successfully processed.'
            },
            {
              date: '2025-04-06T09:15:00Z',
              title: 'Order Processing',
              description: 'Your order is being prepared for shipping.'
            }
          ],
          shippingMethod: 'Standard Shipping',
          paymentMethod: 'Credit Card',
          estimatedDelivery: '2025-04-15',
          shippingAddress: {
            name: 'John Doe',
            street: '123 Main St, Apt 4B',
            city: 'Toronto',
            state: 'ON',
            postalCode: 'M5V 2N4',
            country: 'Canada'
          }
        };

        setOrder(mockOrder);
        setLoading(false);
      }
    }, 1000);
  }, [id]);

  // Format date
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get status badge color
  const getStatusColor = (status: Order['status']) => {
    switch (status) {
      case 'processing':
        return 'blue';
      case 'shipped':
        return 'yellow';
      case 'delivered':
        return 'green';
      case 'cancelled':
        return 'red';
      default:
        return 'gray';
    }
  };

  if (loading) {
    return (

      <Container size="xl" py="xl">
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
          <Loader size="lg" />
        </div>
      </Container>

    );
  }

  if (!order) {
    return (

      <Container size="xl" py="xl">
        <Alert
          icon={<IconAlertCircle size={16} />}
          title="Order not found"
          color="red"
        >
          <Text mb="md">
            We couldn't find the order you're looking for. Please check the order ID or contact customer support.
          </Text>
          <Button
            component={Link}
            to="/my-orders"
            leftSection={<IconArrowLeft size={16} />}
            variant="outline"
          >
            Back to My Orders
          </Button>
        </Alert>
      </Container>

    );
  }

  return (

    <Container size="xl" py="xl">
      <Group mb="xl">
        <div>
          <Button
            component={Link}
            to="/my-orders"
            leftSection={<IconArrowLeft size={16} />}
            variant="subtle"
            mb="sm"
          >
            Back to My Orders
          </Button>
          <Title order={1}>Order Details</Title>
        </div>
        <Group>
          <Button
            variant="outline"
            leftSection={<IconFileInvoice size={16} />}
          >
            Download Invoice
          </Button>
          <Button
            variant="light"
            leftSection={<IconTruckDelivery size={16} />}
            disabled={order.status !== 'shipped'}
          >
            Track Shipment
          </Button>
        </Group>
      </Group>

      <Grid gutter="lg">
        <Grid.Col span={{ base: 12, md: 8 }}>
          <Card withBorder radius="md" mb="lg">
            <Group mb="md">
              <Group>
                <Title order={2}>Order #{order.id}</Title>
                <Badge size="lg" color={getStatusColor(order.status)}>
                  {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                </Badge>
              </Group>
              <Text>Placed on {formatDate(order.date)}</Text>
            </Group>

            <Paper withBorder radius="md" p="md" mb="xl">
              <Title order={3} mb="lg">Order Items</Title>

              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Product</Table.Th>
                    <Table.Th>Price</Table.Th>
                    <Table.Th>Quantity</Table.Th>
                    <Table.Th>Total</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {order.items.map(item => (
                    <Table.Tr key={item.id}>
                      <Table.Td>
                        <Group>
                          <Image
                            src={item.image}
                            width={60}
                            height={60}
                            radius="md"
                            alt={item.name}
                          />
                          <Text component={Link} to={`/products/${item.id}`} style={{ textDecoration: 'none', color: 'inherit' }}>
                            {item.name}
                          </Text>
                        </Group>
                      </Table.Td>
                      <Table.Td>${item.price.toFixed(2)}</Table.Td>
                      <Table.Td>{item.quantity}</Table.Td>
                      <Table.Td>${(item.price * item.quantity).toFixed(2)}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Paper>

            <Paper withBorder radius="md" p="md">
              <Title order={3} mb="lg">Order Timeline</Title>

              <Timeline active={0} bulletSize={24} lineWidth={2}>
                {order.events.map((event, index) => (
                  <Timeline.Item
                    key={index}
                    title={event.title}
                    bullet={<IconCheck size={12} />}
                    lineVariant={index === order.events.length - 1 ? 'dashed' : 'solid'}
                  >
                    <Text size="sm" c="dimmed">{formatDate(event.date)}</Text>
                    <Text size="sm">{event.description}</Text>
                  </Timeline.Item>
                ))}

                {order.status === 'processing' && (
                  <>
                    <Timeline.Item
                      title="Order Shipping"
                      bullet={<IconBox size={12} />}
                      lineVariant="dashed"
                    >
                      <Text size="sm" c="dimmed">Upcoming</Text>
                      <Text size="sm">Your order will be shipped soon.</Text>
                    </Timeline.Item>

                    <Timeline.Item
                      title="Order Delivery"
                      bullet={<IconTruckDelivery size={12} />}
                      lineVariant="dashed"
                    >
                      <Text size="sm" c="dimmed">Estimated: {order.estimatedDelivery}</Text>
                      <Text size="sm">Your order will be delivered to your address.</Text>
                    </Timeline.Item>
                  </>
                )}
              </Timeline>
            </Paper>
          </Card>
        </Grid.Col>

        <Grid.Col span={{ base: 12, md: 4 }}>
          <Stack >
            <Paper withBorder radius="md" p="md">
              <Title order={3} mb="md">Order Summary</Title>

              <Stack >
                <Group >
                  <Text>Subtotal:</Text>
                  <Text fw={500}>
                    ${order.items.reduce((total, item) => total + (item.price * item.quantity), 0).toFixed(2)}
                  </Text>
                </Group>

                <Group >
                  <Text>Shipping:</Text>
                  <Text fw={500}>$12.99</Text>
                </Group>

                <Divider my="sm" />

                <Group >
                  <Text fw={700}>Total:</Text>
                  <Text fw={700} size="lg">${order.total.toFixed(2)}</Text>
                </Group>
              </Stack>
            </Paper>

            <Paper withBorder radius="md" p="md">
              <Group mb="xs">
                <IconUser size={20} />
                <Title order={3} size="h4">Shipping Address</Title>
              </Group>

              <Text>{order.shippingAddress.name}</Text>
              <Text>{order.shippingAddress.street}</Text>
              <Text>
                {order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.postalCode}
              </Text>
              <Text>{order.shippingAddress.country}</Text>
            </Paper>

            <Paper withBorder radius="md" p="md">
              <Stack >
                <Group>
                  <IconTruckDelivery size={20} />
                  <div>
                    <Text fw={500}>Shipping Method</Text>
                    <Text>{order.shippingMethod}</Text>
                  </div>
                </Group>

                <Group>
                  <IconCalendarEvent size={20} />
                  <div>
                    <Text fw={500}>Estimated Delivery</Text>
                    <Text>{order.estimatedDelivery || 'To be determined'}</Text>
                  </div>
                </Group>

                <Group>
                  <IconClipboard size={20} />
                  <div>
                    <Text fw={500}>Payment Method</Text>
                    <Text>{order.paymentMethod}</Text>
                  </div>
                </Group>

                {order.trackingNumber && (
                  <Group>
                    <IconMapPin size={20} />
                    <div>
                      <Text fw={500}>Tracking Number</Text>
                      <Text>{order.trackingNumber}</Text>
                      {order.carrier && <Text size="sm">{order.carrier}</Text>}
                    </div>
                  </Group>
                )}
              </Stack>
            </Paper>

            <Paper withBorder radius="md" p="md">
              <Group >
                <IconShield size={24} color="#228be6" />
                <div>
                  <Text fw={500}>Need Help?</Text>
                  <Text size="sm">
                    If you have any questions or issues with your order,
                    please <Anchor component={Link} to="/help">contact our support team</Anchor>.
                  </Text>
                </div>
              </Group>
            </Paper>

            <Paper withBorder radius="md" p="md">
              <Group >
                <IconPackage size={24} color="#228be6" />
                <div>
                  <Text fw={500}>Consolidate with Other Packages</Text>
                  <Text size="sm">
                    Visit your <Anchor component={Link} to="/packages">Packages dashboard</Anchor> to
                    consolidate this order with other items.
                  </Text>
                </div>
              </Group>
            </Paper>
          </Stack>
        </Grid.Col>
      </Grid>
    </Container>

  );
};

export default OrderDetail;
